/**
 * Floor plan geometry engine.
 *
 * ── How content protection works ─────────────────────────────────────────────
 * `buildGeometry` produces two genuinely different drawings. When `locked` is
 * true it does not emit room names, dimensions, door swings, window detail or
 * any furniture / electrical / plumbing shape — that data never reaches the
 * component, so it cannot be recovered by deleting a CSS blur or an overlay
 * node in devtools. Exactly one sample room stays legible so a buyer can judge
 * the drafting quality.
 *
 * A CSS-only lock would be theatre. This is the mechanism; the watermark and
 * blur layered on top are deterrents.
 *
 * In production the preview must additionally be rasterised server-side and the
 * DWG served from a signed, expiring URL after entitlement is verified.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { feet } from "@/lib/format";
import type { FloorPlan, PlanRoom, RoomType } from "@/types";

export const WALL_OUT = 0.75; // exterior wall thickness, feet
export const WALL_IN = 0.35; // partition half-thickness, feet

export interface Rect {
  x: number;
  y: number;
  w: number;
  d: number;
}

export interface Insets {
  l: number;
  t: number;
  r: number;
  b: number;
}

export type Side = "l" | "t" | "r" | "b";

export interface Shape {
  kind: "rect" | "circle";
  x: number;
  y: number;
  /** rect only */
  w?: number;
  h?: number;
  rx?: number;
  /** circle only */
  r?: number;
}

export interface DoorSpec {
  /** White block that cuts the wall. */
  cut: Rect;
  /** Leaf + swing arc path. */
  path: string;
}

export interface WindowSpec {
  cut: Rect;
  lines: { x1: number; y1: number; x2: number; y2: number }[];
}

export interface DimLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  text: string;
  vertical: boolean;
}

export interface RoomGeometry {
  index: number;
  /** Outer footprint including its share of the walls. */
  rect: Rect;
  /** Clear internal area after wall insets. */
  box: Rect;
  insets: Insets;
  /** Present only when this room is legible to the current viewer. */
  name?: string;
  dimensions?: string;
  type?: RoomType;
  isSample?: boolean;
}

export interface PlanGeometry {
  width: number;
  depth: number;
  locked: boolean;
  rooms: RoomGeometry[];
  doors: DoorSpec[];
  windows: WindowSpec[];
  dimensions: DimLine[];
  furniture: { roomIndex: number; shapes: Shape[] }[];
  electrical: { roomIndex: number; shapes: Shape[]; lines: DimLine[] }[];
  plumbing: { roomIndex: number; path: string; shapes: Shape[] }[];
}

export interface LayerState {
  grid: boolean;
  walls: boolean;
  openings: boolean;
  dimensions: boolean;
  furniture: boolean;
  electrical: boolean;
  plumbing: boolean;
}

export const DEFAULT_LAYERS: LayerState = {
  grid: true,
  walls: true,
  openings: true,
  dimensions: true,
  furniture: true,
  electrical: false,
  plumbing: false,
};

/** Layers that only a licensee may see. */
export const PAID_LAYERS: (keyof LayerState)[] = [
  "openings",
  "dimensions",
  "furniture",
  "electrical",
  "plumbing",
];

/* -------------------------------------------------------------------------- */
/* deterministic randomness                                                   */
/* -------------------------------------------------------------------------- */

function seeded(input: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* -------------------------------------------------------------------------- */
/* layout                                                                     */
/* -------------------------------------------------------------------------- */

interface SliceItem {
  room: PlanRoom;
  index: number;
  rect?: Rect;
}

/**
 * Recursive slice: split the rectangle along its longer axis in proportion to
 * cumulative room area. Produces plausible orthogonal plans that always fill
 * the envelope exactly.
 */
function slice(items: SliceItem[], rect: Rect): void {
  if (items.length === 0) return;
  if (items.length === 1) {
    items[0]!.rect = rect;
    return;
  }

  const total = items.reduce((sum, item) => sum + item.room.a, 0);

  let acc = 0;
  let cut = 0;
  for (let i = 0; i < items.length - 1; i += 1) {
    acc += items[i]!.room.a;
    cut = i;
    if (acc >= total / 2) break;
  }

  const head = items.slice(0, cut + 1);
  const tail = items.slice(cut + 1);
  const ratio = head.reduce((sum, item) => sum + item.room.a, 0) / total;

  if (rect.w >= rect.d) {
    const w = rect.w * ratio;
    slice(head, { x: rect.x, y: rect.y, w, d: rect.d });
    slice(tail, { x: rect.x + w, y: rect.y, w: rect.w - w, d: rect.d });
  } else {
    const d = rect.d * ratio;
    slice(head, { x: rect.x, y: rect.y, w: rect.w, d });
    slice(tail, { x: rect.x, y: rect.y + d, w: rect.w, d: rect.d - d });
  }
}

function edgeInsets(rect: Rect, W: number, D: number): Insets {
  const e = 0.35;
  return {
    l: rect.x <= e ? WALL_OUT : WALL_IN,
    t: rect.y <= e ? WALL_OUT : WALL_IN,
    r: rect.x + rect.w >= W - e ? WALL_OUT : WALL_IN,
    b: rect.y + rect.d >= D - e ? WALL_OUT : WALL_IN,
  };
}

/* -------------------------------------------------------------------------- */
/* openings                                                                   */
/* -------------------------------------------------------------------------- */

function makeDoor(box: Rect, side: Side, at: number, width: number): DoorSpec {
  if (side === "l" || side === "r") {
    const x = side === "l" ? box.x : box.x + box.w;
    const y = box.y + box.d * at - width / 2;
    const dir = side === "l" ? 1 : -1;
    const sweep = side === "l" ? 1 : 0;
    return {
      cut: { x: x - WALL_OUT, y, w: WALL_OUT * 2, d: width },
      path: `M${x} ${y} L${x + dir * width} ${y} A${width} ${width} 0 0 ${sweep} ${x} ${y + width}`,
    };
  }

  const y = side === "t" ? box.y : box.y + box.d;
  const x = box.x + box.w * at - width / 2;
  const dy = side === "t" ? 1 : -1;
  const sweep = side === "t" ? 0 : 1;
  return {
    cut: { x, y: y - WALL_OUT, w: width, d: WALL_OUT * 2 },
    path: `M${x} ${y} L${x} ${y + dy * width} A${width} ${width} 0 0 ${sweep} ${x + width} ${y}`,
  };
}

function makeWindow(box: Rect, side: Side, at: number, length: number): WindowSpec {
  const offsets = [-WALL_OUT * 0.55, 0, WALL_OUT * 0.55];

  if (side === "t" || side === "b") {
    const y = side === "t" ? box.y : box.y + box.d;
    const x0 = box.x + box.w * at - length / 2;
    return {
      cut: { x: x0, y: y - WALL_OUT, w: length, d: WALL_OUT * 2 },
      lines: offsets.map((o) => ({ x1: x0, y1: y + o, x2: x0 + length, y2: y + o })),
    };
  }

  const x = side === "l" ? box.x : box.x + box.w;
  const y0 = box.y + box.d * at - length / 2;
  return {
    cut: { x: x - WALL_OUT, y: y0, w: WALL_OUT * 2, d: length },
    lines: offsets.map((o) => ({ x1: x + o, y1: y0, x2: x + o, y2: y0 + length })),
  };
}

/* -------------------------------------------------------------------------- */
/* furniture                                                                  */
/* -------------------------------------------------------------------------- */

const rect = (x: number, y: number, w: number, h: number, rx = 0.3): Shape => ({
  kind: "rect",
  x,
  y,
  w,
  h,
  rx,
});

const circle = (x: number, y: number, r: number): Shape => ({ kind: "circle", x, y, r });

function furnitureFor(type: RoomType, b: Rect): Shape[] {
  const cx = b.x + b.w / 2;
  const cy = b.y + b.d / 2;

  switch (type) {
    case "bed":
      return [
        rect(cx - 3.2, b.y + 1, 6.4, 7.2, 0.4),
        rect(cx - 3.2, b.y + 1, 6.4, 1.8),
        rect(cx - 4.6, b.y + 1.2, 1.2, 1.6, 0.2),
        rect(cx + 3.4, b.y + 1.2, 1.2, 1.6, 0.2),
        rect(b.x + b.w - 2.2, b.y + b.d - 5, 1.6, 4, 0.2),
      ];
    case "living":
      return [
        rect(cx - 4, b.y + b.d - 4.4, 8, 2.6, 0.5),
        rect(cx - 2.2, cy - 0.9, 4.4, 2.2, 0.4),
        rect(b.x + 1, cy - 1.6, 2.2, 3.4, 0.4),
        rect(cx - 3.4, b.y + 0.9, 6.8, 0.9, 0.2),
      ];
    case "dining":
      return [
        rect(cx - 3, cy - 1.9, 6, 3.8, 0.5),
        ...[-2, 0, 2].flatMap((o) => [circle(cx + o, cy - 2.9, 0.7), circle(cx + o, cy + 2.9, 0.7)]),
      ];
    case "kitchen":
      return [
        rect(b.x + 0.4, b.y + 0.4, b.w - 0.8, 2, 0.2),
        rect(b.x + 0.4, b.y + 0.4, 2, b.d - 0.8, 0.2),
        circle(b.x + 1.4, b.y + 4.4, 0.75),
        circle(b.x + 4.4, b.y + 1.4, 0.7),
      ];
    case "bath":
      return [
        rect(b.x + 0.5, b.y + 0.5, 2.4, 5, 0.4),
        circle(b.x + b.w - 1.6, b.y + 1.4, 0.85),
        rect(b.x + b.w - 2.6, b.y + b.d - 2.4, 2, 1.5, 0.5),
      ];
    case "office":
      return [
        rect(cx - 3.4, cy - 1.4, 6.8, 2.8, 0.3),
        circle(cx, cy + 2.6, 0.8),
        rect(b.x + 0.5, b.y + 0.5, 1.4, b.d - 1, 0.2),
      ];
    case "meeting":
      return [
        rect(cx - 4, cy - 2, 8, 4, 1.6),
        ...[-2.4, 0, 2.4].flatMap((o) => [circle(cx + o, cy - 3, 0.75), circle(cx + o, cy + 3, 0.75)]),
      ];
    case "retail":
      return [
        rect(b.x + 0.6, b.y + 0.6, 1.6, b.d - 1.2, 0.2),
        rect(b.x + b.w - 2.2, b.y + 0.6, 1.6, b.d - 1.2, 0.2),
        rect(cx - 2.5, b.y + b.d - 3, 5, 1.8, 0.3),
      ];
    case "storage":
    case "utility":
      return [
        rect(b.x + 0.5, b.y + 0.5, b.w - 1, 1.6, 0.2),
        rect(b.x + 0.5, b.y + b.d - 2.1, b.w - 1, 1.6, 0.2),
      ];
    default:
      return [];
  }
}

/* -------------------------------------------------------------------------- */
/* build                                                                      */
/* -------------------------------------------------------------------------- */

export interface GeometryOptions {
  locked: boolean;
  layers?: Partial<LayerState>;
  /** Thumbnails skip annotation entirely. */
  thumb?: boolean;
}

export function resolveLayers(locked: boolean, layers?: Partial<LayerState>): LayerState {
  const merged = { ...DEFAULT_LAYERS, ...layers };
  if (!locked) return merged;

  // A locked viewer cannot enable a paid layer, whatever the UI state says.
  const gated = { ...merged };
  for (const key of PAID_LAYERS) gated[key] = false;
  return gated;
}

export function buildGeometry(plan: FloorPlan, options: GeometryOptions): PlanGeometry {
  const { locked, thumb = false } = options;
  const layers = resolveLayers(locked, options.layers);

  const W = plan.dim.w;
  const D = plan.dim.d;
  const random = seeded(plan.id);

  const items: SliceItem[] = plan.rooms.map((room, index) => ({ room, index }));
  slice(items, { x: 0, y: 0, w: W, d: D });

  const sampleIndex = plan.sampleRoom ?? 0;

  const rooms: RoomGeometry[] = [];
  const doors: DoorSpec[] = [];
  const windows: WindowSpec[] = [];
  const furniture: PlanGeometry["furniture"] = [];
  const electrical: PlanGeometry["electrical"] = [];
  const plumbing: PlanGeometry["plumbing"] = [];

  for (const item of items) {
    const outer = item.rect!;
    const insets = edgeInsets(outer, W, D);
    const box: Rect = {
      x: outer.x + insets.l,
      y: outer.y + insets.t,
      w: Math.max(0.5, outer.w - insets.l - insets.r),
      d: Math.max(0.5, outer.d - insets.t - insets.b),
    };

    const isSample = locked && item.index === sampleIndex;
    const legible = !locked || isSample;
    const roomType = (item.room.t ?? "generic") as RoomType;

    const geometry: RoomGeometry = { index: item.index, rect: outer, box, insets };

    // Names, dimensions and room type are withheld unless the viewer may see them.
    if (legible && !thumb && box.w >= 5 && box.d >= 4) {
      geometry.name = item.room.n;
      geometry.dimensions = `${feet(box.w)} × ${feet(box.d)}`;
      geometry.type = roomType;
    }
    if (isSample) geometry.isSample = true;

    rooms.push(geometry);

    /* ---- paid detail: emitted only when the layer survived resolveLayers ---- */
    if (layers.openings && !thumb) {
      const side: Side =
        insets.l === WALL_IN ? "l" : insets.t === WALL_IN ? "t" : insets.r === WALL_IN ? "r" : "b";
      doors.push(makeDoor(box, side, 0.3 + random() * 0.4, roomType === "bath" ? 2.5 : 3));

      if (insets.t === WALL_OUT && box.w > 7) windows.push(makeWindow(box, "t", 0.5, Math.min(box.w * 0.45, 7)));
      if (insets.b === WALL_OUT && box.w > 7) windows.push(makeWindow(box, "b", 0.5, Math.min(box.w * 0.45, 7)));
      if (insets.l === WALL_OUT && box.d > 7) windows.push(makeWindow(box, "l", 0.5, Math.min(box.d * 0.45, 7)));
      if (insets.r === WALL_OUT && box.d > 7) windows.push(makeWindow(box, "r", 0.5, Math.min(box.d * 0.45, 7)));
    }

    const bigEnough = box.w > 6 && box.d > 6;

    // The sample room shows its furniture even while locked — that is the point
    // of the sample. Every other room's furniture stays withheld.
    if ((layers.furniture && !thumb && bigEnough) || (isSample && !thumb && bigEnough)) {
      const shapes = furnitureFor(roomType, box);
      if (shapes.length) furniture.push({ roomIndex: item.index, shapes });
    }

    if (layers.electrical && !thumb) {
      const cx = box.x + box.w / 2;
      const cy = box.y + box.d / 2;
      electrical.push({
        roomIndex: item.index,
        shapes: [circle(cx, cy, 0.75), rect(box.x + 0.7, box.y + 0.7, 1.3, 0.7, 0.2)],
        lines: [
          { x1: cx - 1.1, y1: cy, x2: cx + 1.1, y2: cy, text: "", vertical: false },
          { x1: cx, y1: cy - 1.1, x2: cx, y2: cy + 1.1, text: "", vertical: true },
        ],
      });
    }

    if (layers.plumbing && !thumb && (roomType === "bath" || roomType === "kitchen" || roomType === "utility")) {
      plumbing.push({
        roomIndex: item.index,
        path: `M${box.x + 0.6} ${box.y + box.d - 0.6} L${box.x + box.w - 0.6} ${box.y + box.d - 0.6}`,
        shapes: [circle(box.x + 1.2, box.y + box.d - 0.6, 0.45)],
      });
    }
  }

  /* ---- dimensions ---- */
  const dimensions: DimLine[] = [];
  if (layers.dimensions && !thumb) {
    dimensions.push({ x1: 0, y1: -6, x2: W, y2: -6, text: feet(W), vertical: false });
    dimensions.push({ x1: -6, y1: 0, x2: -6, y2: D, text: feet(D), vertical: true });

    const xs = [
      ...new Set(
        rooms.filter((r) => r.rect.y < 0.5).flatMap((r) => [r.rect.x, r.rect.x + r.rect.w]),
      ),
    ].sort((a, b) => a - b);

    for (let i = 0; i < xs.length - 1; i += 1) {
      const a = xs[i]!;
      const b = xs[i + 1]!;
      if (b - a > 4) {
        dimensions.push({ x1: a, y1: -2.2, x2: b, y2: -2.2, text: feet(b - a), vertical: false });
      }
    }
  }

  return { width: W, depth: D, locked, rooms, doors, windows, dimensions, furniture, electrical, plumbing };
}

/** Rooms a viewer can actually read — the assertion the protection rests on. */
export function legibleRoomCount(geometry: PlanGeometry): number {
  return geometry.rooms.filter((room) => room.name !== undefined).length;
}
